import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DatasetTableActionsProps } from '@databox/ember-integrations/lib/components/Datasets/DatasetTableActions/DatasetTableActions';
import DatasetTableActionsEntryPoint from '@databox/ember-integrations/lib/components/Datasets/DatasetTableActions/DatasetTableActionsEntryPoint';

import type { LineageVerifyTarget, ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type Source from 'webapp/models-custom/source';
import {
	getBillingFeature,
	mapSourceToDataSource,
	mapSyncInfo,
} from 'webapp/models/helpers/react-model-mapper';

interface Args {
	dataSource: Source;
	isActive: boolean;
}

export default class DataManagerConnectedTableRowDatasetActionsComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;
	@service('connect') declare connectService: Service.Connect;
	@service('data-manager') declare dataManagerService: Service.DataManager;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('react') declare reactService: Service.React;

	node: HTMLDivElement | null = null;
	reactElement: ReactIntegrations.Root = null;
	invalidatedGetMetricQuery: (() => void) | null = null;

	get spaceAccessId(): number {
		return this.args.dataSource.numericId!;
	}

	get showBillingPage(): boolean {
		return !this.billingService.advancedDataSourcesEnabled;
	}

	@action
	createDataset(): void {
		if (this.accountService.handleDemo()) {
			return;
		}
		this.connectService.addNewDataset();
	}

	@action
	handleOnNavigateToMetrics(spaceAccessId: number, type?: string) {
		if (type) {
			this.navigationService.transitionToRoute(
				'metric-library.source.specific',
				type,
				spaceAccessId,
			);
		} else {
			this.navigationService.transitionToRoute('metric-library');
		}
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLDivElement;
		this.reactElement = this.reactService.create(this.node);

		this.renderReactComponent();

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderReactComponent);
		this.eventManagerService.on(AppEvent.VERIFICATION_CHANGED, this, this.renderReactComponent);
		this.eventManagerService.on(AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED, this, () => {
			this.reactService.unmount(this.reactElement);
			this.reactElement = this.reactService.create(node as HTMLDivElement);
			this.renderReactComponent();
		});
	}

	renderReactComponent() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: DatasetTableActionsProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			isWhiteLabeled: this.accountService.whiteLabelingEnabled,
			isAccountTypeAgency: this.accountService.isAgency,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			onViewDataset: (id: number) => this.datasetEventsService.handleViewDataset(`${id}`),
			onLineageNavigate: (kind: string, id: string, intent?: 'view' | 'edit') =>
				this.datasetEventsService.handleLineageNavigate(kind, id, intent),
			onLineageVerify: (target: LineageVerifyTarget) =>
				this.datasetEventsService.handleLineageVerify(target),
			onEditDataset: (id: number) => this.datasetEventsService.handleEditDataset(`${id}`),
			onExportError: this.datasetEventsService.handleExportError,
			onDataExport: this.datasetEventsService.handleDataExport,
			onDatasetDeleted: () => {
				this.datasetEventsService.handleDatasetDeleted();
				// the id stays pending: the pusher ignores the record for only a short while, so a late re-add must stay hidden
				void this.args.dataSource.delete(true);
			},
			onCreateMetrics: (id: number) =>
				this.datasetEventsService.handleMetricCreate(
					`${id}`,
					this.invalidatedGetMetricQuery,
				),
			onValidationError: this.datasetEventsService.handleValidationError,
			data: {
				dataSource: {
					...mapSourceToDataSource(
						this.args.dataSource,
						this.currentUserService.numericId,
					),
					syncInfo: mapSyncInfo(this.args.dataSource.lastSync),
				},
				dataSourceType: this.args.dataSource.sourceType,
			},
			isLoading: false,
			// hide the row as soon as the user confirms; it is restored if the delete fails
			onDeleteStart: () =>
				this.dataManagerService.markPendingDeletion(this.args.dataSource.id!),
			onDeleteSourceError: () => {
				this.dataManagerService.clearPendingDeletion(this.args.dataSource.id!);
				this.datasetEventsService.handleDeleteSourceError();
			},
			isParentHovered: this.args.isActive,
			onDataRefresh: this.datasetEventsService.handleDataRefresh,
			onDuplicateDataSourceError: this.datasetEventsService.handleDuplicateDataSourceError,
			onSourceDuplicated: this.datasetEventsService.handleSourceDuplicated,
			getInvalidatedGetMetricQuery: (invalidatedGetMetricQuery) => {
				this.invalidatedGetMetricQuery = invalidatedGetMetricQuery;
			},
			onConnectionUpdated: this.datasetEventsService.handleOnConnectionUpdated,
			onApiError: (error: ReactIntegrations.Error) =>
				this.datasetEventsService.handleApiError(error),
			onDatasetFinishSetupSuccess: this.datasetEventsService.handleDatasetFinishSetupSuccess,
			onOpenActivityLog: this.datasetEventsService.openActivityLogCallback,
			onPlanUpgrade: async (featureName: string) => {
				return await this.billingService.handlePlanUpgradeForFeature(
					getBillingFeature(featureName),
				);
			},
			getInvalidatedBillingInfoQuery: (invalidatedBillingInfoQuery) => {
				this.billingService.invalidateFeatureCache = invalidatedBillingInfoQuery;
			},
			onWatchVideo: (videoUrl: string) =>
				this.datasetEventsService.handleWatchVideo(videoUrl),
			onVerificationToggle: (verificationInfo: Record<string, unknown>, objectName: string) =>
				this.datasetEventsService.handleVerificationToggle(verificationInfo, objectName),
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				DatasetTableActionsEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	updateReactComponent(): void {
		this.renderReactComponent();
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);

		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.VERIFICATION_CHANGED, this);
		this.eventManagerService.off(AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED, this);
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{didUpdate this.updateReactComponent @isActive}}
		/>
	</template>
}
